import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, RotateCcw, ExternalLink, Sparkles, CheckCircle2, CalendarCheck } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const STREAMS = {
  science: { label: "Science & Engineering", desc: "You love logic, problem-solving and understanding how things work. Explore engineering, data science, research and technology.", color: "#2b8cf0" },
  medical: { label: "Medical & Life Sciences", desc: "You care about people and biology. Explore medicine, allied health, biotechnology and research.", color: "#e5484d" },
  commerce: { label: "Commerce & Business", desc: "You think strategically about value and growth. Explore finance, management, entrepreneurship and economics.", color: "#16a34a" },
  creative: { label: "Creative & Design", desc: "You express ideas visually and think originally. Explore design, media, architecture and the arts.", color: "#9333ea" },
  humanities: { label: "Humanities & Social", desc: "You understand people, society and ideas. Explore law, psychology, education and social sciences.", color: "#F58220" },
};

const QUESTIONS = [
  { q: "Which activity sounds most enjoyable on a free afternoon?", a: [
    { t: "Solving a tricky puzzle or coding challenge", s: "science" },
    { t: "Reading about the human body or nature", s: "medical" },
    { t: "Planning a small business or budgeting", s: "commerce" },
    { t: "Sketching, editing videos or making music", s: "creative" },
    { t: "Debating ideas or helping a friend", s: "humanities" },
  ]},
  { q: "At school, which subject feels most natural to you?", a: [
    { t: "Physics & Mathematics", s: "science" },
    { t: "Biology & Chemistry", s: "medical" },
    { t: "Economics & Accounts", s: "commerce" },
    { t: "Fine Arts & Literature", s: "creative" },
    { t: "History & Political Science", s: "humanities" },
  ]},
  { q: "How do you prefer to solve a problem?", a: [
    { t: "Break it into logical steps", s: "science" },
    { t: "Research and experiment carefully", s: "medical" },
    { t: "Weigh costs, benefits and outcomes", s: "commerce" },
    { t: "Imagine a brand-new approach", s: "creative" },
    { t: "Talk it through with people", s: "humanities" },
  ]},
  { q: "Which compliment would make you happiest?", a: [
    { t: "You're brilliant at analysis", s: "science" },
    { t: "You really care about others' wellbeing", s: "medical" },
    { t: "You have great business sense", s: "commerce" },
    { t: "You're so creative and original", s: "creative" },
    { t: "You understand people deeply", s: "humanities" },
  ]},
  { q: "Pick a dream project to work on:", a: [
    { t: "Build a robot or an app", s: "science" },
    { t: "Find a cure or study ecosystems", s: "medical" },
    { t: "Launch a startup", s: "commerce" },
    { t: "Design a game or a film", s: "creative" },
    { t: "Lead a community campaign", s: "humanities" },
  ]},
  { q: "Which environment energises you most?", a: [
    { t: "A lab or tech workspace", s: "science" },
    { t: "A hospital or research centre", s: "medical" },
    { t: "A busy office or market", s: "commerce" },
    { t: "A studio or creative space", s: "creative" },
    { t: "A classroom, court or NGO", s: "humanities" },
  ]},
  { q: "What matters most in your future career?", a: [
    { t: "Innovation and discovery", s: "science" },
    { t: "Helping and healing people", s: "medical" },
    { t: "Growth and financial success", s: "commerce" },
    { t: "Self-expression and originality", s: "creative" },
    { t: "Justice and social impact", s: "humanities" },
  ]},
  { q: "Which school club would you join?", a: [
    { t: "Robotics / Coding club", s: "science" },
    { t: "Science / Eco club", s: "medical" },
    { t: "Entrepreneurship / Finance club", s: "commerce" },
    { t: "Drama / Art / Music club", s: "creative" },
    { t: "Debate / Social service club", s: "humanities" },
  ]},
];

export default function Quiz() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);

  const total = QUESTIONS.length;
  const progress = Math.round(((done ? total : step) / total) * 100);

  const choose = (s) => {
    const next = { ...answers, [step]: s };
    setAnswers(next);
    if (step < total - 1) setStep(step + 1);
    else setDone(true);
  };

  const result = () => {
    const tally = {};
    Object.values(answers).forEach((s) => (tally[s] = (tally[s] || 0) + 1));
    const top = Object.entries(tally).sort((a, b) => b[1] - a[1])[0];
    return top ? top[0] : "science";
  };

  const reset = () => { setStep(0); setAnswers({}); setDone(false); };

  useEffect(() => {
    if (done) {
      try { localStorage.setItem("cc_quiz", JSON.stringify({ result: STREAMS[result()].label, at: new Date().toISOString() })); } catch (e) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  const bookWithResult = () => {
    const r = STREAMS[result()].label;
    try { localStorage.setItem("cc_quiz", JSON.stringify({ result: r, at: new Date().toISOString() })); } catch (e) {}
    navigate("/", { state: { scrollTo: "contact", quizResult: r } });
  };

  return (
    <div className="min-h-screen bg-[#f6f8fc] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-[760px] mx-auto px-5 py-12 md:py-16">
          <div className="text-center mb-8">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F58220]/10 text-[#F58220] text-xs font-semibold font-head">
              <Sparkles size={14} /> FREE CAREER-INTEREST QUIZ
            </span>
            <h1 className="font-head text-3xl md:text-4xl font-bold text-[#16244F] mt-4">Find Your Career Direction</h1>
            <p className="text-[#5a6274] mt-3">Answer {total} quick questions to discover the streams that fit your interests.</p>
          </div>

          {!done ? (
            <div className="bg-white rounded-2xl cc-shadow-card p-7 md:p-10">
              <div className="flex items-center justify-between mb-2 text-sm text-[#8a90a0] font-head">
                <span>Question {step + 1} of {total}</span>
                <span>{progress}%</span>
              </div>
              <div className="h-2 rounded-full bg-[#eef1f6] mb-7 overflow-hidden">
                <div className="h-full bg-[#F58220] transition-all duration-500" style={{ width: `${((step) / total) * 100}%` }} />
              </div>

              <h2 className="font-head text-xl md:text-2xl font-semibold text-[#16244F] mb-6">{QUESTIONS[step].q}</h2>
              <div className="space-y-3">
                {QUESTIONS[step].a.map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => choose(opt.s)}
                    className={`w-full text-left px-5 py-4 rounded-xl border-2 transition-all font-medium text-[#16244F] hover:border-[#2b8cf0] hover:bg-[#eaf3ff] ${
                      answers[step] === opt.s ? "border-[#2b8cf0] bg-[#eaf3ff]" : "border-gray-100 bg-white"
                    }`}
                  >
                    {opt.t}
                  </button>
                ))}
              </div>

              {step > 0 && (
                <button onClick={() => setStep(step - 1)} className="mt-6 inline-flex items-center gap-1.5 text-[#5a6274] hover:text-[#16244F] font-head text-sm">
                  <ArrowLeft size={16} /> Back
                </button>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-2xl cc-shadow-card p-7 md:p-10 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#eaf3ff] text-[#2b8cf0] mb-5">
                <CheckCircle2 size={38} />
              </div>
              <p className="text-[#5a6274] font-head">Your strongest match is</p>
              <h2 className="font-head text-3xl font-bold mt-1" style={{ color: STREAMS[result()].color }}>
                {STREAMS[result()].label}
              </h2>
              <p className="text-[#5a6274] mt-4 max-w-lg mx-auto">{STREAMS[result()].desc}</p>

              <div className="mt-8 rounded-xl bg-[#f6f8fc] p-5 text-left">
                <p className="text-sm text-[#5a6274]">
                  This quick quiz is just the beginning. For a complete, counselor-reviewed report mapping
                  aptitude, personality, interest, intelligence and values, take the full
                  <strong className="text-[#16244F]"> BrightIQ</strong> assessment.
                </p>
              </div>

              <div className="mt-7 flex flex-wrap justify-center gap-4">
                <button onClick={bookWithResult} className="inline-flex items-center gap-2 bg-[#F58220] hover:bg-[#e0730f] text-white font-head font-semibold px-6 py-3.5 rounded-lg transition-colors">
                  <CalendarCheck size={18} /> Book a Counselling Session
                </button>
                <a href="https://brightiq.in" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border-2 border-[#16244F] text-[#16244F] hover:bg-[#16244F] hover:text-white font-head font-semibold px-6 py-3 rounded-lg transition-colors">
                  Full Assessment on BrightIQ <ExternalLink size={16} />
                </a>
                <button onClick={reset} className="inline-flex items-center gap-2 text-[#5a6274] hover:text-[#16244F] font-head font-semibold px-4 py-3">
                  <RotateCcw size={16} /> Retake
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
